import { useState } from 'react';
import { components } from 'react-select';
import ReactSelectCreatable from 'react-select/creatable';

import { MultiValue } from 'components/shared/form/InfiniteScrollDropdown';
import { customTheme, getDefaultStyles } from 'components/shared/form/Select/customStyles';
import DropdownCaretIcon from 'svg/DropdownCaretIcon';
import DropdownClearIcon from 'svg/DropdownClearIcon';

export type CreatableSelectType = {
  // Existing values to offer. Plain strings: this control's value IS the text, not a record id.
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  dataTestId?: string;
};

type CreatableOption = { label: string; value: string };

const toCreatableOption = (value: string): CreatableOption => ({ label: value, value });

const indicatorComponents = {
  MultiValue,
  DropdownIndicator: (props: any) => (
    <components.DropdownIndicator {...props}>
      <DropdownCaretIcon />
    </components.DropdownIndicator>
  ),
  ClearIndicator: (props: any) => (
    <components.ClearIndicator {...props}>
      <DropdownClearIcon />
    </components.ClearIndicator>
  ),
};

/**
 * A dropdown that also accepts anything typed into it, for fields where the existing values are a
 * convenience rather than a constraint — pick one of the labels already in use, or coin a new one.
 *
 * Value in and out is the plain string, because that is what such a field stores. Matching against
 * the existing options is case-insensitive, so typing a value that already exists in another casing
 * offers the existing one rather than promising a duplicate.
 *
 * Text left in the box commits on blur. Left to react-select, it would be discarded silently: a
 * value typed and then submitted directly would post as empty, and on an edit form the typing would
 * never mark the form dirty, so Save would stay disabled with nothing to explain it.
 */
export default function CreatableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  dataTestId,
}: CreatableSelectType) {
  // Controlled so blur can still see what was typed — react-select clears its own input first.
  const [inputValue, setInputValue] = useState('');

  const commit = (next: string) => {
    setInputValue('');
    onChange(next);
  };

  const select = (
    <ReactSelectCreatable<CreatableOption>
      options={options.map(toCreatableOption)}
      value={value ? toCreatableOption(value) : null}
      inputValue={inputValue}
      // Only what the user typed. The clear react-select fires on blur and on select is ignored, so
      // the text is still here when onBlur runs; commit() is what clears it.
      onInputChange={(next, meta) => {
        if (meta.action === 'input-change') setInputValue(next);
      }}
      onBlur={() => {
        const typed = inputValue.trim();
        if (!typed) return;

        // An existing label wins on its stored spelling, matching both the menu and the way the
        // server resolves a typed label.
        const existing = options.find((option) => option.toLowerCase() === typed.toLowerCase());
        commit(existing ?? typed);
      }}
      onChange={(option) => commit(option?.value ?? '')}
      onCreateOption={(input) => commit(input.trim())}
      formatCreateLabel={(input) => `Add "${input.trim()}"`}
      isValidNewOption={(input, _value, selectOptions) => {
        const typed = input.trim();
        if (!typed) return false;

        return !selectOptions.some(
          (option) => (option as CreatableOption).label?.toLowerCase() === typed.toLowerCase()
        );
      }}
      placeholder={placeholder}
      isDisabled={disabled}
      isClearable
      components={indicatorComponents}
      styles={getDefaultStyles()}
      theme={customTheme}
    />
  );

  if (dataTestId) return <div data-testid={dataTestId}>{select}</div>;

  return select;
}

export type CreatableMultiSelectType = {
  options: string[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  dataTestId?: string;
  menuPortalTarget?: HTMLElement;
};

/**
 * The multi-value variant: same type-to-add semantics, but each committed value becomes a chip and
 * the field holds a list. Case-insensitive matching applies both against the offered options and
 * against what is already selected, so re-adding a value in another casing is a no-op rather than a
 * duplicate chip.
 */
export function CreatableMultiSelect({
  options,
  values,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  dataTestId,
  menuPortalTarget,
}: CreatableMultiSelectType) {
  const [inputValue, setInputValue] = useState('');

  const addValue = (raw: string) => {
    setInputValue('');
    const typed = raw.trim();
    if (!typed) return;

    const existing = options.find((option) => option.toLowerCase() === typed.toLowerCase());
    const next = existing ?? typed;
    if (values.some((value) => value.toLowerCase() === next.toLowerCase())) return;
    onChange([...values, next]);
  };

  const select = (
    <ReactSelectCreatable<CreatableOption, true>
      isMulti
      options={options.map(toCreatableOption)}
      value={values.map(toCreatableOption)}
      inputValue={inputValue}
      onInputChange={(next, meta) => {
        if (meta.action === 'input-change') setInputValue(next);
      }}
      onBlur={() => addValue(inputValue)}
      onChange={(selected) => {
        setInputValue('');
        onChange((selected ?? []).map((option) => option.value));
      }}
      onCreateOption={addValue}
      formatCreateLabel={(input) => `Add "${input.trim()}"`}
      isValidNewOption={(input, selectedValues, selectOptions) => {
        const typed = input.trim();
        if (!typed) return false;

        const taken = (option: unknown) => (option as CreatableOption).label?.toLowerCase() === typed.toLowerCase();
        return !selectOptions.some(taken) && !selectedValues.some(taken);
      }}
      placeholder={placeholder}
      isDisabled={disabled}
      isClearable
      components={indicatorComponents}
      styles={{
        ...getDefaultStyles(),
        ...(menuPortalTarget ? { menuPortal: (base) => ({ ...base, zIndex: 9999 }) } : {}),
      }}
      theme={customTheme}
      menuPortalTarget={menuPortalTarget}
    />
  );

  if (dataTestId) return <div data-testid={dataTestId}>{select}</div>;

  return select;
}
